import React, { useState, useEffect, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, GraduationCap, Heart, Ship, Compass, FileCheck, Plane, Send, 
  User, Mail, MessageCircle, CheckCircle, AlertCircle
} from 'lucide-react';

const SERVICES = [
  {
    id: 'corporate',
    icon: <Briefcase size={24} />,
    title: 'Corporate & Business',
    desc: 'Coordinated flights, accommodation, and ground logistics for executives and teams. We manage the complexities of corporate travel so you can focus on your business.'
  },
  {
    id: 'student',
    icon: <GraduationCap size={24} />,
    title: 'Student Admissions',
    desc: 'Guidance for university applications, visa documentation, and arrival logistics. We help students transition smoothly to their new academic environments.'
  },
  {
    id: 'honeymoon',
    icon: <Heart size={24} />,
    title: 'Honeymoon Packages',
    desc: 'Curated itineraries, private transfers, and premium accommodation for couples. Every detail is planned to ensure a seamless and memorable romantic escape.'
  },
  {
    id: 'cruise',
    icon: <Ship size={24} />,
    title: 'Cruises & Leisure',
    desc: 'Ocean and river cruise bookings, alongside tailored leisure and wellness retreats. We help you select the right vessel, cabin, and shore excursions.'
  },
  {
    id: 'custom',
    icon: <Compass size={24} />,
    title: 'Custom Travel Booking',
    desc: 'End-to-end planning for solo, family, and group trips. From flight comparisons to hotel reservations, we build itineraries around your budget and schedule.'
  },
  {
    id: 'visa',
    icon: <FileCheck size={24} />,
    title: 'Visa & Ancillary Support',
    desc: 'Document review, travel insurance recommendations, and airport transfer coordination. We ensure your paperwork is complete and your journey is stress-free.'
  }
];

const FAQS = [
  { q: 'How do I book a corporate travel package?', a: 'Contact our team with your travel dates, destination, and team size. We will provide a customized proposal covering flights, accommodation, and ground logistics tailored to your corporate policy.' },
  { q: 'Do you assist with student visas?', a: 'Yes, we provide document checklists, review your application for completeness, and guide you through the submission process. ' },
  { q: 'Are your honeymoon packages customizable?', a: 'Yes. Every itinerary is tailored to your preferences, including accommodation upgrades, private tours, and specific dining experiences. We work with you to design a trip that matches your exact vision.' },
  { q: 'What ancillary services do you provide?', a: 'We assist with travel insurance recommendations, airport meet-and-greet services, and ground transportation coordination to ensure a seamless journey from departure to return.' }
];

const Services = ({ isDarkMode = false }) => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const [activeForm, setActiveForm] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '', email: '', phone: '',
    companyName: '', destination: '', departureDate: '', teamSize: '', requirements: '',
    targetCountry: '', course: '', educationLevel: '', startDate: '', questions: '',
    duration: '', returnDate: '', requests: '',
    region: '', departureMonth: '', cabinType: '', travelers: '', notes: '',
    interests: '', country: '', visaType: '', travelDate: '', services: ''
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const previousBackground = document.body.style.backgroundColor;
    const previousColor = document.body.style.color;
    const previousColorScheme = document.body.style.colorScheme;

    document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
    document.body.style.color = isDarkMode ? '#E9F6F3' : '#132E29';
    document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';

    return () => {
      document.body.style.backgroundColor = previousBackground;
      document.body.style.color = previousColor;
      document.body.style.colorScheme = previousColorScheme;
    };
  }, [isDarkMode]);

  const validate = useCallback(() => {
    const errs = {};
    if (!formData.fullName.trim()) errs.fullName = 'Full name is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Valid email required';
    if (!formData.phone.trim()) errs.phone = 'Phone number required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const createWhatsAppURL = () => {
    const serviceTitle = SERVICES.find(s => s.id === activeForm)?.title || 'Service';
    let msg = `*New ${serviceTitle} Request*\n\n`;
    msg += `*Name:* ${formData.fullName}\n*Email:* ${formData.email || '—'}\n*Phone:* ${formData.phone}\n`;
    
    if (activeForm === 'corporate') {
      msg += `*Company:* ${formData.companyName || '—'}\n*Destination:* ${formData.destination || '—'}\n*Travel Date:* ${formData.departureDate || '—'}\n*Team Size:* ${formData.teamSize || '—'}\n*Requirements:* ${formData.requirements || 'None'}\n`;
    } else if (activeForm === 'student') {
      msg += `*Target Country:* ${formData.targetCountry || '—'}\n*Course:* ${formData.course || '—'}\n*Level:* ${formData.educationLevel || '—'}\n*Start Date:* ${formData.startDate || '—'}\n*Questions:* ${formData.questions || 'None'}\n`;
    } else if (activeForm === 'honeymoon') {
      msg += `*Destination:* ${formData.destination || '—'}\n*Dates:* ${formData.departureDate || '—'} to ${formData.returnDate || '—'}\n*Duration:* ${formData.duration || '—'} days\n*Requests:* ${formData.requests || 'None'}\n`;
    } else if (activeForm === 'cruise') {
      msg += `*Region:* ${formData.region || '—'}\n*Departure:* ${formData.departureMonth || '—'}\n*Cabin:* ${formData.cabinType || '—'}\n*Travelers:* ${formData.travelers || '—'}\n*Notes:* ${formData.notes || 'None'}\n`;
    } else if (activeForm === 'custom') {
      msg += `*Destination:* ${formData.destination || '—'}\n*Dates:* ${formData.departureDate || '—'} to ${formData.returnDate || '—'}\n*Travelers:* ${formData.travelers || '—'}\n*Interests:* ${formData.interests || 'None'}\n`;
    } else if (activeForm === 'visa') {
      msg += `*Country:* ${formData.country || '—'}\n*Visa Type:* ${formData.visaType || '—'}\n*Travel Date:* ${formData.travelDate || '—'}\n*Services:* ${formData.services || 'None'}\n`;
    }
    
    msg += `\n_Sent via Oscar Global Web_`;
    return `https://wa.me/2348077854009?text=${encodeURIComponent(msg)}`;
  };

  const safelyOpenWhatsApp = (url) => {
    try {
      const win = window.open(url, '_blank', 'noopener,noreferrer,width=540,height=800');
      if (win) { win.focus(); return true; }
      const a = document.createElement('a');
      a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.style.display = 'none'; document.body.appendChild(a);
      a.click(); setTimeout(() => document.body.removeChild(a), 50);
      return true;
    } catch { return false; }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    const url = createWhatsAppURL();
    const success = safelyOpenWhatsApp(url);
    setStatus(success ? 'success' : 'error');
    if (success) {
      setTimeout(() => {
        setActiveForm(null);
        setStatus('idle');
        setFormData({
          fullName: '', email: '', phone: '', companyName: '', destination: '', departureDate: '', teamSize: '', requirements: '',
          targetCountry: '', course: '', educationLevel: '', startDate: '', questions: '', duration: '', returnDate: '', requests: '',
          region: '', departureMonth: '', cabinType: '', travelers: '', notes: '', interests: '', country: '', visaType: '', travelDate: '', services: ''
        });
      }, 2000);
    }
  };

  const reveal = (delay = 0) =>
    reduceMotion ? {} : {
      initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 },
      viewport: { once: true, margin: '0px 0px -10% 0px' },
      transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }
    };

  const heroReveal = reduceMotion ? {} : {
    initial: { opacity: 0, y: 26 }, animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

    .services-root { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: var(--bg); color: var(--text); min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility; }
    .services-root * { box-sizing: border-box; }
    .services-root h1, .services-root h2, .services-root h3 { font-family: 'Sora', 'Inter', sans-serif; color: var(--text); }
    .services-root p { color: var(--text-muted); }
    .services-root :focus-visible { outline: 3px solid var(--focus); outline-offset: 3px; border-radius: 8px; }

    .container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
    .container.narrow { max-width: 880px; }
    .section { padding-block: clamp(4rem, 8vw, 6.75rem); }
    .sectiona { margin-top: 4rem}
    .hero { padding-top: clamp(8rem, 12vw, 10rem); text-align: center; margin-bottom: 2rem; }
    
    .eyebrow { display: inline-flex; align-items: center; gap: 0.55rem; padding: 0.48rem 0.85rem; border-radius: 999px; border: 1px solid var(--border-strong); background: var(--surface); color: var(--primary); font-size: 0.78rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; }
    .h1 { font-size: clamp(2.5rem, 5vw, 3.75rem); line-height: 1.1; letter-spacing: -0.03em; margin: 1.25rem 0 1.5rem; font-weight: 700; }
    .lead { font-size: 1.125rem; line-height: 1.7; color: var(--text-muted); max-width: 640px; margin: 0 auto; }
    .h2 { font-size: clamp(1.75rem, 3vw, 2.25rem); line-height: 1.15; letter-spacing: -0.02em; margin: 0 0 1rem; font-weight: 700; }

    .services-grid { display: grid; gap: 1.5rem; grid-template-columns: 1fr; }
    @media (min-width: 640px) { .services-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (min-width: 1024px) { .services-grid { grid-template-columns: repeat(3, 1fr); } }

    .service-card { background: var(--surface); border: 1px solid var(--border); border-radius: 24px; padding: 2rem; box-shadow: var(--shadow); backdrop-filter: blur(14px); transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease; display: flex; flex-direction: column; gap: 1rem; height: 100%; cursor: pointer; }
    .service-card:hover { transform: translateY(-6px); box-shadow: 0 24px 60px rgba(15, 118, 110, 0.12); border-color: var(--border-strong); }
    .service-icon { width: 56px; height: 56px; border-radius: 16px; display: grid; place-items: center; color: var(--primary); background: linear-gradient(135deg, rgba(15, 118, 110, 0.12), rgba(124, 58, 237, 0.08)); border: 1px solid var(--border); }
    .service-card h3 { font-size: 1.25rem; font-weight: 600; margin: 0; }
    .service-card p { margin: 0; line-height: 1.65; font-size: 0.95rem; }

    .faq-list { display: grid; gap: 1rem; margin-top: 2.5rem; }
    .faq-item { background: var(--surface); border: 1px solid var(--border); border-radius: 20px; overflow: hidden; box-shadow: var(--shadow); transition: border-color 0.2s ease; }
    .faq-item[open] { border-color: var(--border-strong); }
    .faq-item summary { list-style: none; cursor: pointer; padding: 1.35rem 1.5rem; font-weight: 600; color: var(--text); display: flex; justify-content: space-between; align-items: center; gap: 1rem; font-size: 1.05rem; }
    .faq-item summary::-webkit-details-marker { display: none; }
    .faq-item summary::after { content: '+'; font-size: 1.5rem; color: var(--primary); transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1); flex-shrink: 0; font-weight: 400; line-height: 1; }
    .faq-item[open] summary::after { transform: rotate(45deg); }
    .faq-item p { margin: 0; padding: 0 1.5rem 1.5rem; line-height: 1.72; font-size: 0.95rem; color: var(--text-muted); }

    .cta-card { background: linear-gradient(135deg, rgba(15, 118, 110, 0.16), rgba(124, 58, 237, 0.14)); border: 1px solid var(--border-strong); border-radius: 28px; padding: clamp(2.5rem, 5vw, 4rem); text-align: center; box-shadow: var(--shadow); backdrop-filter: blur(14px); }
    .cta-card p { max-width: 56ch; margin: 0 auto 2rem; font-size: 1.05rem; }
    .btn-primary { min-height: 52px; border-radius: 999px; padding: 0.9rem 1.8rem; background: linear-gradient(135deg, var(--btn-from), var(--btn-to)); color: #ffffff; font-size: 1rem; font-weight: 700; font-family: 'Inter', sans-serif; display: inline-flex; align-items: center; justify-content: center; gap: 0.6rem; cursor: pointer; border: 1px solid transparent; box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28); transition: transform 0.25s ease, box-shadow 0.25s ease; text-decoration: none; }
    .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36); }

    /* Modal & Form Styles */
    .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 2rem; overflow-y: auto; }
    .modal-content { background: var(--surface); border: 1px solid var(--border); border-radius: 24px; padding: 2.5rem; max-width: 640px; width: 100%; box-shadow: var(--shadow); position: relative; max-height: 90vh; overflow-y: auto; }
    .modal-close { position: absolute; top: 1rem; right: 1rem; background: transparent; border: none; color: var(--text-muted); cursor: pointer; font-size: 1.5rem; width: 32px; height: 32px; display: grid; place-items: center; border-radius: 50%; transition: background 0.2s; }
    .modal-close:hover { background: var(--bg-muted); }
    .modal-title { font-size: 1.5rem; font-weight: 700; margin-bottom: 1.5rem; color: var(--text); }

    .form-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
    @media (min-width: 640px) { .form-grid { grid-template-columns: 1fr 1fr; } .form-group.full { grid-column: 1 / -1; } }
    .form-group { display: flex; flex-direction: column; gap: 0.5rem; }
    .form-label { font-size: 0.875rem; font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 0.5rem; }
    .form-label .req { color: var(--accent); }
    .form-input, .form-select, .form-textarea { width: 100%; padding: 0.875rem 1rem; border-radius: 14px; border: 1px solid var(--border-strong); background: var(--bg-muted); color: var(--text); font-size: 0.95rem; font-family: inherit; transition: all 0.2s ease; outline: none; }
    .form-input:focus, .form-select:focus, .form-textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 4px var(--focus); background: var(--surface); }
    .form-input.error, .form-select.error, .form-textarea.error { border-color: #ef4444; box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12); }
    .form-select { appearance: none; background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='M6 9l6 6 6-6'/%3e%3c/svg%3e"); background-repeat: no-repeat; background-position: right 1rem center; background-size: 16px; padding-right: 2.5rem; }
    .form-textarea { resize: vertical; min-height: 100px; }
    .form-error { font-size: 0.8rem; color: #ef4444; display: flex; align-items: center; gap: 0.35rem; margin-top: -0.25rem; }
    
    .btn-submit { width: 100%; padding: 1rem; border-radius: 999px; background: linear-gradient(135deg, var(--btn-from), var(--btn-to)); color: #fff; font-size: 1rem; font-weight: 700; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.6rem; box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28); transition: all 0.25s ease; margin-top: 1.5rem; }
    .btn-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36); }
    .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
    
    @keyframes spin { to { transform: rotate(360deg); } }
    .spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.8s linear infinite; }
    .status-msg { padding: 1rem 1.25rem; border-radius: 16px; display: flex; align-items: flex-start; gap: 0.75rem; margin-bottom: 1.5rem; font-size: 0.9rem; font-weight: 500; line-height: 1.5; }
    .status-msg.success { background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.25); color: #10b981; }
    .status-msg.error { background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.25); color: #ef4444; }
    .status-msg a { color: inherit; text-decoration: underline; font-weight: 600; }

    @media (max-width: 640px) { .container { width: calc(100% - 2rem); } }
  `;

  return (
    <main
      className="services-root"
      style={{
        '--bg': isDarkMode ? '#061110' : '#F7FBFA',
        '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
        '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
        '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
        '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
        '--text': isDarkMode ? '#E9F6F3' : '#132E29',
        '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
        '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
        '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
        '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
        '--accent': isDarkMode ? '#F59E0B' : '#B45309',
        '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
        '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
      }}
    >
      <style>{globalStyles}</style>

      <section className="hero sectiona" aria-labelledby="services-heading">
        <div className="container">
          <motion.div {...heroReveal}>
            <span className="eyebrow"><Plane size={14} aria-hidden="true" /> Our Services</span>
            <h1 id="services-heading" className="h1">Travel and Student Admission Services</h1>
            <p className="lead">Coordinated by a responsive team. From corporate logistics to romantic escapes, we handle the planning and paperwork so you can focus on the journey.</p>
          </motion.div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }} aria-labelledby="grid-heading">
        <div className="container">
          <h2 id="grid-heading" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Our Services</h2>
          <div className="services-grid">
            {SERVICES.map((service, index) => (
              <motion.article
                key={service.title}
                className="service-card"
                {...reveal(index * 0.06)}
                onClick={() => setActiveForm(service.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveForm(service.id); }}
              >
                <div className="service-icon" aria-hidden="true">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--bg-muted)' }} aria-labelledby="faq-heading">
        <div className="container narrow">
          <motion.div {...reveal()}>
            <h2 id="faq-heading" className="h2" style={{ textAlign: 'center' }}>Questions, answered plainly</h2>
          </motion.div>
          <div className="faq-list">
            {FAQS.map((faq, index) => (
              <motion.details key={faq.q} className="faq-item" {...reveal(index * 0.05)}>
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="cta-heading">
        <div className="container narrow">
          <motion.div className="cta-card" {...reveal()}>
            <h2 id="cta-heading" className="h2">Ready to plan your next trip?</h2>
            <p>Tell us about your travel requirements. Our team will review your request and get back to you with a clear, customized proposal.</p>
            <button className="btn-primary" onClick={() => navigate('/booking')}>
              <Send size={18} aria-hidden="true" /> Start your booking
            </button>
          </motion.div>
        </div>
      </section>

      {activeForm && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setActiveForm(null)}>
          <div className="modal-content">
            <button className="modal-close" onClick={() => setActiveForm(null)} aria-label="Close form">×</button>
            <h3 className="modal-title">Inquire about {SERVICES.find(s => s.id === activeForm)?.title}</h3>

            {status === 'success' && (
              <div className="status-msg success" role="alert">
                <CheckCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div><strong>Request sent!</strong><br />Your inquiry is open in WhatsApp. Tap Send to confirm with our team.</div>
              </div>
            )}

            {status === 'error' && (
              <div className="status-msg error" role="alert">
                <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div><strong>Failed to open WhatsApp.</strong><br />Ensure pop-ups are allowed, or <a href="https://wa.me/2348077854009" target="_blank" rel="noopener noreferrer">contact us manually</a>.</div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label"><User size={16} /> Full Name <span className="req">*</span></label>
                  <input name="fullName" value={formData.fullName} onChange={handleChange} className={`form-input ${errors.fullName ? 'error' : ''}`} />
                  {errors.fullName && <span className="form-error"><AlertCircle size={14} /> {errors.fullName}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label"><Mail size={16} /> Email</label>
                  <input name="email" type="email" value={formData.email} onChange={handleChange} className={`form-input ${errors.email ? 'error' : ''}`} />
                  {errors.email && <span className="form-error"><AlertCircle size={14} /> {errors.email}</span>}
                </div>

                <div className="form-group full">
                  <label className="form-label"><MessageCircle size={16} /> Phone <span className="req">*</span></label>
                  <input name="phone" type="tel" value={formData.phone} onChange={handleChange} className={`form-input ${errors.phone ? 'error' : ''}`} />
                  {errors.phone && <span className="form-error"><AlertCircle size={14} /> {errors.phone}</span>}
                </div>

                {activeForm === 'corporate' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Company Name</label>
                      <input name="companyName" value={formData.companyName} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Destination</label>
                      <input name="destination" value={formData.destination} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Travel Date</label>
                      <input name="departureDate" type="date" value={formData.departureDate} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Team Size</label>
                      <input name="teamSize" type="number" value={formData.teamSize} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Requirements</label>
                      <textarea name="requirements" value={formData.requirements} onChange={handleChange} className="form-textarea" />
                    </div>
                  </>
                )}

                {activeForm === 'student' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Target Country</label>
                      <input name="targetCountry" value={formData.targetCountry} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Preferred Course</label>
                      <input name="course" value={formData.course} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Education Level</label>
                      <select name="educationLevel" value={formData.educationLevel} onChange={handleChange} className="form-select">
                        <option value="">Select</option>
                        <option value="High School">High School</option>
                        <option value="Bachelor's">Bachelor's</option>
                        <option value="Master's">Master's</option>
                        <option value="PhD">PhD</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Intended Start Date</label>
                      <input name="startDate" type="date" value={formData.startDate} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Questions/Notes</label>
                      <textarea name="questions" value={formData.questions} onChange={handleChange} className="form-textarea" />
                    </div>
                  </>
                )}

                {activeForm === 'honeymoon' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Destination</label>
                      <input name="destination" value={formData.destination} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Duration (Days)</label>
                      <input name="duration" type="number" value={formData.duration} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Departure Date</label>
                      <input name="departureDate" type="date" value={formData.departureDate} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Return Date</label>
                      <input name="returnDate" type="date" value={formData.returnDate} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Special Requests</label>
                      <textarea name="requests" value={formData.requests} onChange={handleChange} className="form-textarea" />
                    </div>
                  </>
                )}

                {activeForm === 'cruise' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Preferred Region</label>
                      <input name="region" value={formData.region} onChange={handleChange} className="form-input" placeholder="e.g. Caribbean, Mediterranean" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Departure Month</label>
                      <input name="departureMonth" type="month" value={formData.departureMonth} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Cabin Preference</label>
                      <select name="cabinType" value={formData.cabinType} onChange={handleChange} className="form-select">
                        <option value="">Select</option>
                        <option value="Interior">Interior</option>
                        <option value="Oceanview">Oceanview</option>
                        <option value="Balcony">Balcony</option>
                        <option value="Suite">Suite</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Travelers</label>
                      <input name="travelers" type="number" value={formData.travelers} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Notes</label>
                      <textarea name="notes" value={formData.notes} onChange={handleChange} className="form-textarea" />
                    </div>
                  </>
                )}

                {activeForm === 'custom' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Destination</label>
                      <input name="destination" value={formData.destination} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Travelers</label>
                      <input name="travelers" type="number" value={formData.travelers} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Departure Date</label>
                      <input name="departureDate" type="date" value={formData.departureDate} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Return Date</label>
                      <input name="returnDate" type="date" value={formData.returnDate} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Interests/Requirements</label>
                      <textarea name="interests" value={formData.interests} onChange={handleChange} className="form-textarea" />
                    </div>
                  </>
                )}

                {activeForm === 'visa' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Destination Country</label>
                      <input name="country" value={formData.country} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Visa Type</label>
                      <select name="visaType" value={formData.visaType} onChange={handleChange} className="form-select">
                        <option value="">Select</option>
                        <option value="Tourist">Tourist</option>
                        <option value="Student">Student</option>
                        <option value="Work">Work</option>
                        <option value="Business">Business</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Intended Travel Date</label>
                      <input name="travelDate" type="date" value={formData.travelDate} onChange={handleChange} className="form-input" />
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Services Needed</label>
                      <textarea name="services" value={formData.services} onChange={handleChange} className="form-textarea" placeholder="e.g. Document review, insurance, airport transfer" />
                    </div>
                  </>
                )}
              </div>

              <button type="submit" disabled={status === 'submitting'} className="btn-submit">
                {status === 'submitting' ? (
                  <><span className="spinner" /> Opening WhatsApp…</>
                ) : (
                  <><Send size={18} /> Send Request via WhatsApp</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default Services;





























// import React, { useEffect } from 'react';
// import { motion, useReducedMotion } from 'framer-motion';
// import { useNavigate } from 'react-router-dom';
// import { 
//   Briefcase, GraduationCap, Heart, Ship, Compass, FileCheck,  Plane, Send
// } from 'lucide-react';

// const SERVICES = [
//   {
//     icon: <Briefcase size={24} />,
//     title: 'Corporate & Business',
//     desc: 'Coordinated flights, accommodation, and ground logistics for executives and teams. We manage the complexities of corporate travel so you can focus on your business.'
//   },
//   {
//     icon: <GraduationCap size={24} />,
//     title: 'Student Admissions',
//     desc: 'Guidance for university applications, visa documentation, and arrival logistics. We help students transition smoothly to their new academic environments.'
//   },
//   {
//     icon: <Heart size={24} />,
//     title: 'Honeymoon Packages',
//     desc: 'Curated itineraries, private transfers, and premium accommodation for couples. Every detail is planned to ensure a seamless and memorable romantic escape.'
//   },
//   {
//     icon: <Ship size={24} />,
//     title: 'Cruises & Leisure',
//     desc: 'Ocean and river cruise bookings, alongside tailored leisure and wellness retreats. We help you select the right vessel, cabin, and shore excursions.'
//   },
//   {
//     icon: <Compass size={24} />,
//     title: 'Custom Travel Booking',
//     desc: 'End-to-end planning for solo, family, and group trips. From flight comparisons to hotel reservations, we build itineraries around your budget and schedule.'
//   },
//   {
//     icon: <FileCheck size={24} />,
//     title: 'Visa & Ancillary Support',
//     desc: 'Document review, travel insurance recommendations, and airport transfer coordination. We ensure your paperwork is complete and your journey is stress-free.'
//   }
// ];

// const FAQS = [
//   {
//     q: 'How do I book a corporate travel package?',
//     a: 'Contact our team with your travel dates, destination, and team size. We will provide a customized proposal covering flights, accommodation, and ground logistics tailored to your corporate policy.'
//   },
//   {
//     q: 'Do you assist with student visas?',
//     a: 'Yes, we provide document checklists, review your application for completeness, and guide you through the submission process. '
//   },
//   {
//     q: 'Are your honeymoon packages customizable?',
//     a: 'Yes. Every itinerary is tailored to your preferences, including accommodation upgrades, private tours, and specific dining experiences. We work with you to design a trip that matches your exact vision.'
//   },
//   {
//     q: 'What ancillary services do you provide?',
//     a: 'We assist with travel insurance recommendations, airport meet-and-greet services, and ground transportation coordination to ensure a seamless journey from departure to return.'
//   }
// ];

// const Services = ({ isDarkMode = false }) => {
//   const navigate = useNavigate();
//   const reduceMotion = useReducedMotion();

//   useEffect(() => {
//     const previousBackground = document.body.style.backgroundColor;
//     const previousColor = document.body.style.color;
//     const previousColorScheme = document.body.style.colorScheme;

//     document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
//     document.body.style.color = isDarkMode ? '#E9F6F3' : '#132E29';
//     document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';

//     return () => {
//       document.body.style.backgroundColor = previousBackground;
//       document.body.style.color = previousColor;
//       document.body.style.colorScheme = previousColorScheme;
//     };
//   }, [isDarkMode]);

//   const reveal = (delay = 0) =>
//     reduceMotion
//       ? {}
//       : {
//           initial: { opacity: 0, y: 22 },
//           whileInView: { opacity: 1, y: 0 },
//           viewport: { once: true, margin: '0px 0px -10% 0px' },
//           transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }
//         };

//   const heroReveal = reduceMotion
//     ? {}
//     : {
//         initial: { opacity: 0, y: 26 },
//         animate: { opacity: 1, y: 0 },
//         transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
//       };

//   const globalStyles = `
//     @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

//     .services-root {
//       font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
//       background: var(--bg);
//       color: var(--text);
//       min-height: 100vh;
//       overflow-x: hidden;
//       -webkit-font-smoothing: antialiased;
//       text-rendering: optimizeLegibility;
//     }

//     .services-root * { box-sizing: border-box; }
//     .services-root h1, .services-root h2, .services-root h3 { font-family: 'Sora', 'Inter', sans-serif; color: var(--text); }
//     .services-root p { color: var(--text-muted); }

//     .services-root :focus-visible {
//       outline: 3px solid var(--focus);
//       outline-offset: 3px;
//       border-radius: 8px;
//     }

//     .container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
//     .container.narrow { max-width: 880px; }
//     .section { padding-block: clamp(4rem, 8vw, 6.75rem); }
//     .sectiona { margin-top: 4rem}

//     .hero { padding-top: clamp(8rem, 12vw, 10rem); text-align: center; margin-bottom: 2rem; }
    
//     .eyebrow {
//       display: inline-flex; align-items: center; gap: 0.55rem;
//       padding: 0.48rem 0.85rem; border-radius: 999px;
//       border: 1px solid var(--border-strong); background: var(--surface);
//       color: var(--primary); font-size: 0.78rem; font-weight: 700;
//       letter-spacing: 0.14em; text-transform: uppercase;
//     }

//     .h1 {
//       font-size: clamp(2.5rem, 5vw, 3.75rem); line-height: 1.1;
//       letter-spacing: -0.03em; margin: 1.25rem 0 1.5rem; font-weight: 700;
//     }

//     .lead {
//       font-size: 1.125rem; line-height: 1.7; color: var(--text-muted);
//       max-width: 640px; margin: 0 auto;
//     }

//     .h2 {
//       font-size: clamp(1.75rem, 3vw, 2.25rem); line-height: 1.15;
//       letter-spacing: -0.02em; margin: 0 0 1rem; font-weight: 700;
//     }

//     .services-grid {
//       display: grid; gap: 1.5rem; grid-template-columns: 1fr;
//     }

//     @media (min-width: 640px) { .services-grid { grid-template-columns: repeat(2, 1fr); } }
//     @media (min-width: 1024px) { .services-grid { grid-template-columns: repeat(3, 1fr); } }

//     .service-card {
//       background: var(--surface); border: 1px solid var(--border);
//       border-radius: 24px; padding: 2rem; box-shadow: var(--shadow);
//       backdrop-filter: blur(14px); transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
//       display: flex; flex-direction: column; gap: 1rem; height: 100%;
//     }

//     .service-card:hover {
//       transform: translateY(-6px);
//       box-shadow: 0 24px 60px rgba(15, 118, 110, 0.12);
//       border-color: var(--border-strong);
//     }

//     .service-icon {
//       width: 56px; height: 56px; border-radius: 16px;
//       display: grid; place-items: center; color: var(--primary);
//       background: linear-gradient(135deg, rgba(15, 118, 110, 0.12), rgba(124, 58, 237, 0.08));
//       border: 1px solid var(--border);
//     }

//     .service-card h3 { font-size: 1.25rem; font-weight: 600; margin: 0; }
//     .service-card p { margin: 0; line-height: 1.65; font-size: 0.95rem; }

//     .faq-list { display: grid; gap: 1rem; margin-top: 2.5rem; }

//     .faq-item {
//       background: var(--surface); border: 1px solid var(--border);
//       border-radius: 20px; overflow: hidden; box-shadow: var(--shadow);
//       transition: border-color 0.2s ease;
//     }

//     .faq-item[open] { border-color: var(--border-strong); }

//     .faq-item summary {
//       list-style: none; cursor: pointer; padding: 1.35rem 1.5rem;
//       font-weight: 600; color: var(--text); display: flex;
//       justify-content: space-between; align-items: center; gap: 1rem;
//       font-size: 1.05rem;
//     }

//     .faq-item summary::-webkit-details-marker { display: none; }

//     .faq-item summary::after {
//       content: '+'; font-size: 1.5rem; color: var(--primary);
//       transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1); flex-shrink: 0;
//       font-weight: 400; line-height: 1;
//     }

//     .faq-item[open] summary::after { transform: rotate(45deg); }

//     .faq-item p {
//       margin: 0; padding: 0 1.5rem 1.5rem; line-height: 1.72;
//       font-size: 0.95rem; color: var(--text-muted);
//     }

//     .cta-card {
//       background: linear-gradient(135deg, rgba(15, 118, 110, 0.16), rgba(124, 58, 237, 0.14));
//       border: 1px solid var(--border-strong); border-radius: 28px;
//       padding: clamp(2.5rem, 5vw, 4rem); text-align: center;
//       box-shadow: var(--shadow); backdrop-filter: blur(14px);
//     }

//     .cta-card p { max-width: 56ch; margin: 0 auto 2rem; font-size: 1.05rem; }

//     .btn-primary {
//       min-height: 52px; border-radius: 999px; padding: 0.9rem 1.8rem;
//       background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
//       color: #ffffff; font-size: 1rem; font-weight: 700; font-family: 'Inter', sans-serif;
//       display: inline-flex; align-items: center; justify-content: center; gap: 0.6rem;
//       cursor: pointer; border: 1px solid transparent;
//       box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28);
//       transition: transform 0.25s ease, box-shadow 0.25s ease; text-decoration: none;
//     }

//     .btn-primary:hover {
//       transform: translateY(-2px);
//       box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36);
//     }

//     @media (max-width: 640px) { .container { width: calc(100% - 2rem); } }
//   `;

//   return (
//     <main
//       className="services-root"
//       style={{
//         '--bg': isDarkMode ? '#061110' : '#F7FBFA',
//         '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
//         '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
//         '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
//         '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
//         '--text': isDarkMode ? '#E9F6F3' : '#132E29',
//         '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
//         '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
//         '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
//         '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
//         '--accent': isDarkMode ? '#F59E0B' : '#B45309',
//         '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
//         '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
//       }}
//     >
//       <style>{globalStyles}</style>

//       {/* HERO */}
//       <section className="hero sectiona" aria-labelledby="services-heading">
//         <div className="container">
//           <motion.div {...heroReveal}>
//             <span className="eyebrow">
//               <Plane size={14} aria-hidden="true" /> Our Services
//             </span>
//             <h1 id="services-heading" className="h1">
//              Travel and Student Admission Services
//             </h1>
//             <p className="lead">
//               Coordinated by a responsive team. From corporate logistics to romantic escapes, 
//               we handle the planning and paperwork so you can focus on the journey.
//             </p>
//           </motion.div>
//         </div>
//       </section>

//       {/* SERVICES GRID */}
//       <section className="section" style={{ paddingTop: 0 }} aria-labelledby="grid-heading">
//         <div className="container">
//           <h2 id="grid-heading" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
//             Our Services
//           </h2>
//           <div className="services-grid">
//             {SERVICES.map((service, index) => (
//               <motion.article
//                 key={service.title}
//                 className="service-card"
//                 {...reveal(index * 0.06)}
//               >
//                 <div className="service-icon" aria-hidden="true">
//                   {service.icon}
//                 </div>
//                 <h3>{service.title}</h3>
//                 <p>{service.desc}</p>
//               </motion.article>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* FAQ SECTION */}
//       <section className="section" style={{ background: 'var(--bg-muted)' }} aria-labelledby="faq-heading">
//         <div className="container narrow">
//           <motion.div {...reveal()}>
//             <h2 id="faq-heading" className="h2" style={{ textAlign: 'center' }}>
//               Questions, answered plainly
//             </h2>
//           </motion.div>

//           <div className="faq-list">
//             {FAQS.map((faq, index) => (
//               <motion.details key={faq.q} className="faq-item" {...reveal(index * 0.05)}>
//                 <summary>{faq.q}</summary>
//                 <p>{faq.a}</p>
//               </motion.details>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* CTA SECTION */}
//       <section className="section" aria-labelledby="cta-heading">
//         <div className="container narrow">
//           <motion.div className="cta-card" {...reveal()}>
//             <h2 id="cta-heading" className="h2">Ready to plan your next trip?</h2>
//             <p>
//               Tell us about your travel requirements. Our team will review your request 
//               and get back to you with a clear, customized proposal.
//             </p>
//             <button className="btn-primary" onClick={() => navigate('/booking')}>
//               <Send size={18} aria-hidden="true" />
//               Start your booking
//             </button>
//           </motion.div>
//         </div>
//       </section>
//     </main>
//   );
// };

// export default Services;